import React, { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useBranding } from "../../context/BrandingContext";

import OrgPortalHeader from "../../components/organization/portal/OrgPortalHeader";
import OrgPortalFooter from "../../components/organization/portal/OrgPortalFooter";
import OrgPortalBanner from "../../components/organization/portal/OrgPortalBanner";

import DivisionHeader from "../../components/division/DivisionHeader";
import DivisionSummary from "../../components/division/DivisionSummary";
import OrgPortalDivisionTabs from "../../components/organization/portal/OrgPortalDivisionTabs";

import NoData from "../../components/common/NoData";
import Partners from "../../components/organization/portal/Partners";
import VideoSection from "../../components/video/VideoSection";
import useTranslation from "../../i18n/useTranslation";
import GetPublicDivisionDetail from "../../components/division/queries/GetPublicDivisionDetail";
import ActiveSeason from "../../components/season/ActiveSeason";

export default function OrgPortalDivisionPage() {
    const { divisionSlug, seasonSlug } = useParams();
    const { branding, theme } = useBranding();
    const {language} = useTranslation();
    const navigate = useNavigate();

    const [getDivisionDetails] = GetPublicDivisionDetail();

    const [division, setDivision] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [activeSeasonData, setActiveSeasonData] = useState(null);
    const [teams, setTeams] = useState([]);
    const [games, setGames] = useState([]);
    const [standings] = useState([]);
    const [totalGames, setTotalGames] = useState(0);
    const loadedDivisionSlugRef = useRef(null);

    const safeTheme = useMemo(() => {
        const colors = theme?.colors || {};

        return {
            primary: colors.primary || "#14151A",
            primaryColor: colors.primary || "#14151A",
            secondary: colors.secondary || "#14151A",
            secondaryColor: colors.secondary || "#14151A",
            accent: colors.accent || "#D5FF5F",
            accentColor: colors.accent || "#D5FF5F",
            background: colors.background || "#05070A",
            backgroundColor: colors.background || "#05070A",
            surface: colors.surface || "#0B1220",
            surfaceColor: colors.surface || "#0B1220",
            border: colors.border || "rgba(255,255,255,0.06)",
            cardBorder: colors.border || "rgba(255,255,255,0.06)",
            textOnBackground: colors.textOnBackground || "#E5E7EB",
            textOnSurface: colors.textOnSurface || "#F9FAFB",
            textOnPrimary: colors.textOnPrimary || "#F9FAFB",
            textOnSecondary: colors.textOnSecondary || "#F9FAFB",
        };
    }, [theme]);

    useEffect(() => {
        if (!divisionSlug || loadedDivisionSlugRef.current === divisionSlug) return;
        loadedDivisionSlugRef.current = divisionSlug;

        getDivisionDetails({
            variables: {
                divisionSlug
            },
        })
            .then((r) => {
                if (r.data?.portalDivision) {
                    setDivision(r.data.portalDivision);
                    setSeasons(r.data.portalDivision.seasons || []);
                    setActiveSeasonData(r.data.portalDivision.activeSeason || null);
                    setTeams(r.data.portalDivision.activeSeason?.teams || []);
                }
            })
            .catch((err) => {
                loadedDivisionSlugRef.current = null;
                console.error("Error fetching division details:", err);
            });
    }, [getDivisionDetails, divisionSlug]);

    useEffect(() => {
        if (seasons.length > 0 && seasonSlug) {
            const season = seasons.find(s => s.slug === seasonSlug);
            if (season) {
                setActiveSeasonData(season);
                setTeams(season.teams || []);
            }
        }
    }, [seasons, seasonSlug]);

    if (!branding) return null;

    return (
        <div
            style={{
                minHeight: "100vh",
                fontFamily: "Poppins, sans-serif",
                backgroundColor: safeTheme.backgroundColor,
                color: safeTheme.textOnBackground,
            }}
        >
            {/* PUBLIC HEADER */}
            <OrgPortalHeader
                org={division?.organization}
                logo={branding?.logoUrl}
                theme={safeTheme}
            />

            <OrgPortalBanner />

            <div
                style={{
                    maxWidth: 1200,
                    margin: "40px auto",
                    padding: "0 20px",
                }}
            >
                {/* DIVISION HEADER */}
                {division && (
                    <DivisionHeader
                        division={division}
                        activeSeasonData={activeSeasonData}
                    />
                )}

                {/* ACTIVE SEASON */}
                {division && seasons.length > 0 && (
                    <ActiveSeason
                        activeSeasonData={activeSeasonData}
                        seasons={seasons}
                        setSeasons={setSeasons}
                        setActiveSeasonData={setActiveSeasonData}
                        setTeams={setTeams}
                        division={division}
                        readyOldSeason={false}
                        colors={safeTheme}
                        onSeasonChange={(season) => navigate(`/division/${divisionSlug}/${season.slug}`)}
                        disableAdd={true}
                    />
                )}

                {/* SUMMARY */}
                <div style={{ marginTop: 20 }}>
                    <DivisionSummary
                        teamsCount={teams?.length}
                        activeSeasonData={activeSeasonData}
                        totalGames={totalGames}
                        colors={safeTheme}
                    />
                </div>

                {/* TABS */}
                {activeSeasonData ? (
                    <div style={{ marginTop: 20 }}>
                        <OrgPortalDivisionTabs
                            teams={teams}
                            setTeams={setTeams}
                            games={games}
                            setGames={setGames}
                            standings={standings}
                            division={division}
                            activeSeasonData={activeSeasonData}
                            setTotalGames={setTotalGames}
                        />
                    </div>
                ) : (
                    <NoData />
                )}
            </div>

            <Partners theme={safeTheme}/>

            <VideoSection
                key={language}
                src={`/videos/kbs-banner-${language}.mp4`}
                autoPlay
                loop
                muted
                align="center"
                maxWidth="1040px"
                background="transparent"
            />

            {/* PUBLIC FOOTER */}
            <OrgPortalFooter branding={branding} theme={safeTheme} />
        </div>
    );
}
